/**
 * Vuzlyk icon set — Heroicons-flavored, hand-tuned for the IDE feel.
 *
 * Ported from ``design/ui.jsx``. Each icon is an inline SVG that
 * inherits ``color`` via ``stroke="currentColor"`` / ``fill="currentColor"``,
 * so they pick up whatever colour the surrounding text uses.
 *
 * Add new icons sparingly; the design system intentionally limits the
 * vocabulary so screens stay visually consistent.
 */

import type { SVGProps } from "react";

type IconProps = SVGProps<SVGSVGElement>;

const stroke = (path: JSX.Element | JSX.Element[], strokeWidth = 1.6) =>
  function StrokeIcon(props: IconProps): JSX.Element {
    return (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        {...props}
      >
        {path}
      </svg>
    );
  };

const fill = (path: JSX.Element | JSX.Element[]) =>
  function FillIcon(props: IconProps): JSX.Element {
    return (
      <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
        {path}
      </svg>
    );
  };

export const Icon = {
  hub: stroke(
    <>
      <circle cx="12" cy="12" r="2.5" />
      <circle cx="5" cy="6" r="1.6" />
      <circle cx="19" cy="6" r="1.6" />
      <circle cx="5" cy="18" r="1.6" />
      <circle cx="19" cy="18" r="1.6" />
      <path d="m6.5 7 4 3.5M17.5 7l-4 3.5M6.5 17l4-3.5M17.5 17l-4-3.5" />
    </>,
  ),
  note: stroke(
    <>
      <path d="M5 4h10l4 4v12H5z" />
      <path d="M15 4v4h4" />
      <path d="M8 12h7M8 15h7M8 18h4" />
    </>,
  ),
  cal: stroke(
    <>
      <rect x="4" y="5" width="16" height="15" rx="2" />
      <path d="M4 9h16M9 3v4M15 3v4" />
    </>,
  ),
  habit: stroke(
    <>
      <path d="M3 12c5-7 13-7 18 0-5 7-13 7-18 0z" />
      <circle cx="12" cy="12" r="3" />
    </>,
  ),
  settings: stroke(
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1A1.7 1.7 0 0 0 4.6 9a1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
    </>,
  ),
  plus: stroke(<path d="M12 5v14M5 12h14" />, 1.8),
  search: stroke(
    <>
      <circle cx="11" cy="11" r="6.5" />
      <path d="m20 20-3.5-3.5" />
    </>,
  ),
  check: stroke(<path d="m5 13 4 4L19 7" />, 2.5),
  link: stroke(
    <>
      <path d="M10 14a3.5 3.5 0 0 0 5 0l4-4a3.5 3.5 0 0 0-5-5l-1 1" />
      <path d="M14 10a3.5 3.5 0 0 0-5 0l-4 4a3.5 3.5 0 0 0 5 5l1-1" />
    </>,
    1.8,
  ),
  chevL: stroke(<path d="m15 6-6 6 6 6" />, 2),
  chevR: stroke(<path d="m9 6 6 6-6 6" />, 2),
  menu: stroke(<path d="M4 6h16M4 12h16M4 18h16" />, 1.8),
  filter: stroke(<path d="M3 5h18l-7 9v6l-4-2v-4z" />),
  fire: fill(
    <path d="M12 2c1 4 4 5 4 9a4 4 0 1 1-8 0c0-2 1-3 1-5 0 2 2 3 3 1-1-2 0-3 0-5z" />,
  ),
  tg: fill(
    <path d="m21.5 4-3 14c-.2 1-.9 1.3-1.8.8L12 15l-2.4 2.3c-.3.3-.5.5-1 .5l.4-4.8L17.7 6c.4-.3-.1-.5-.6-.2L7 12 2.6 10.7c-1-.3-1-.9.2-1.4L20.3 3c.8-.3 1.5.2 1.2 1z" />,
  ),
  more: fill(
    <>
      <circle cx="5" cy="12" r="1.5" />
      <circle cx="12" cy="12" r="1.5" />
      <circle cx="19" cy="12" r="1.5" />
    </>,
  ),
  // ─── Vuzlyk brand mark ───
  // Source: design/brand-mark/Icon.brand.jsx — "вузлик-у-вузлику"
  // (twin-knot rhombus). Outer hooked diamond + two stacked inner
  // rhombi reading as a slip-knot. Inherits the active theme accent
  // via ``currentColor``.
  //
  // Use ``Icon.brand`` at ≥20px. Below 16px (favicons, dense lists),
  // use ``Icon.brandTiny`` — cardinal ticks dropped, inner diamonds
  // filled, stroke bumped to 1.8 so the mark stays crisp.
  brand: stroke(
    <>
      <path d="M12 4 20 12 12 20 4 12z" />
      <path d="M12 7 14 9 12 11 10 9z" />
      <path d="M12 13 14 15 12 17 10 15z" />
      <path d="M12 4V2.5" />
      <path d="M20 12h1.5" />
      <path d="M12 20v1.5" />
      <path d="M4 12H2.5" />
    </>,
  ),
  brandTiny: function BrandTiny(props: IconProps): JSX.Element {
    return (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.8}
        strokeLinecap="round"
        strokeLinejoin="round"
        {...props}
      >
        <path d="M12 4 20 12 12 20 4 12z" />
        <path d="M12 7 14 9 12 11 10 9z" fill="currentColor" />
        <path d="M12 13 14 15 12 17 10 15z" fill="currentColor" />
      </svg>
    );
  },
  hash: stroke(<path d="M5 9h14M5 15h14M10 4 8 20M16 4l-2 16" />),
  task: stroke(
    <>
      <rect x="4" y="4" width="7" height="7" rx="1.5" />
      <path d="m6 7.5 1.5 1.5L10 6.5" />
      <path d="M14 6h6M14 10h4M14 14h6M14 18h4" />
    </>,
  ),
  cart: stroke(
    <>
      <path d="M3 4h2l2 12h11l2-8H7" />
      <circle cx="9" cy="20" r="1.4" />
      <circle cx="17" cy="20" r="1.4" />
    </>,
  ),
  chef: stroke(
    <>
      <path d="M6 11a4 4 0 1 1 3-7 3.5 3.5 0 0 1 6 0 4 4 0 1 1 3 7v3H6z" />
      <path d="M6 14h12v4a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2z" />
      <path d="M10 17v0M14 17v0" />
    </>,
  ),
  flag: fill(
    <path d="M5 3v18h1.5v-7.5h11l-2-3.5 2-3.5h-11V3z" />,
  ),
  clock: stroke(
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </>,
  ),
  user: stroke(
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21c1-4 4.5-6 8-6s7 2 8 6" />
    </>,
  ),
  people: stroke(
    <>
      <circle cx="9" cy="9" r="3" />
      <circle cx="17" cy="10" r="2.4" />
      <path d="M3 19c0-3 2.7-5 6-5s6 2 6 5" />
      <path d="M14 18c.4-2.2 2-3.5 4-3.5s2.7 1 3 2.5" />
    </>,
  ),
  pin: fill(<path d="M14 2 22 10 18 11 17 18 13 14 7 20 6 19 12 13 8 9 9 8 16 7z" />),
  edit: stroke(<path d="M14 4l6 6-10 10H4v-6z" />, 1.8),
} as const;

export type IconName = keyof typeof Icon;
